import {
  EntitlementGroupInterface,
  LeihBarPermissions,
  PermissionEnum,
  RelationType
} from '@thro-types/access-handler';
import { OrderWithConflicts, Product, ProductType, TermsOfAcceptance } from '@thro-types/leihbar';
import { User } from '@thro-types/users';

import { isAfter, isBefore, startOfMonth } from 'date-fns';
import { Gateway } from 'index';

import { S3Response } from 'types/AppTypes';
import { useAuthContext } from 'utils/hooks/useAuthContext';

interface S3UploadProps {
  file: Blob | string;
  type: 'ProductImage' | 'OptionImage' | 'Icon';
}

export const uploadAttachmentToS3 = async ({ file, type }: S3UploadProps) => {
  const token = localStorage.getItem('jwt-token');
  const refreshToken = localStorage.getItem('refresh-token');

  let pathOfType = 'unknown';

  switch (type) {
    case 'Icon':
      pathOfType = 'categoryIcon';
      break;
    case 'ProductImage':
      pathOfType = 'productimage';
      break;
    case 'OptionImage':
      pathOfType = 'optionimage';
      break;
    default:
      break;
  }

  const formData = new FormData();
  let response: string[] = [];
  if (file instanceof Blob) {
    formData.append('file', file);
  } else {
    response.push(file);
  }

  const { data }: { data: S3Response[] } = await Gateway.post(
    `/leihbar/v1/s3/upload/${pathOfType}`,
    formData,
    {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        refresh: refreshToken || ''
      }
    }
  );

  response.push(
    ...data.map(file => {
      return file.Key;
    })
  );
  return response;
};

export const canUserView = (
  product: Product,
  permissions: PermissionEnum[],
  currentUser: User,
  entitlementGroups: EntitlementGroupInterface[]
) => {
  if (canUserEdit(product, permissions, currentUser, entitlementGroups)) {
    return true;
  }

  if (
    product.visibilityGroupIds?.some(groupId => entitlementGroups.some(eg => eg.id === groupId))
  ) {
    return true;
  }

  if (product.targetUserIds?.includes(currentUser.id)) {
    return true;
  }

  // This is required for users that are not Lenders with the `LeihBarPermissions.LEIHBAR_PRODUCT_EDIT_OWN` but have the lendergroupId of the product
  if (isLenderOfProduct(product, entitlementGroups)) {
    return true;
  }

  return false;
};

export const canUserEdit = (
  product: Product,
  permissions: PermissionEnum[],
  currentUser: User,
  entitlementGroups: EntitlementGroupInterface[]
) => {
  if (isLeihBarAdmin(permissions)) {
    return true;
  } else if (
    // user is lender of the product
    permissions?.includes(LeihBarPermissions.LEIHBAR_PRODUCT_EDIT_OWN) &&
    (product.lender?.email === currentUser?.email || isLenderOfProduct(product, entitlementGroups))
  ) {
    return true;
  } else if (
    // user is editor of the product
    product.editors?.some(editor => editor.email === currentUser?.email)
  ) {
    return true;
  } else {
    return false;
  }
};

export const usePermissionToCreate = () => {
  const { permissions } = useAuthContext();
  if (permissions?.includes(LeihBarPermissions.LEIHBAR_PRODUCT_CREATE)) {
    return true;
  }
  return false;
};

export const isLeihBarAdmin = (permissions: PermissionEnum[]) => {
  if (permissions?.includes(LeihBarPermissions.LEIHBAR_PRODUCT_EDIT_ANY)) {
    return true;
  }
  return false;
};

export const isLeihBarLender = (permissions: PermissionEnum[]) => {
  if (permissions?.includes(LeihBarPermissions.LEIHBAR_PRODUCT_EDIT_OWN)) {
    return true;
  }
  return false;
};

export const isProductABundle = (product?: Product) => {
  return product?.productType === ProductType.BUNDLE && !!product?.products?.length;
};

export const hasAtLeastOneLeihbarEntitlement = (entitlementGroups: EntitlementGroupInterface[]) => {
  return entitlementGroups.some(
    entitlementGroup => entitlementGroup.relationType === RelationType.LEIHBAR
  );
};

export const getMyLeihbarEntitlements = (entitlementGroups: EntitlementGroupInterface[]) => {
  return entitlementGroups.filter(
    entitlementGroup => entitlementGroup.relationType === RelationType.LEIHBAR
  );
};

/**
 * Checks if Today is after 15.3 and the current accepted Date before 15.3
 * @param leihbarTermsOfAcceptance
 * @returns
 */
export const areTermsAccepted = (leihbarTermsOfAcceptance: TermsOfAcceptance | undefined) => {
  const today = new Date();
  const getTargetDateForMonth = (month: number, day: number) => {
    const year =
      new Date().getMonth() < month ? new Date().getFullYear() - 1 : new Date().getFullYear();
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  const march15 = getTargetDateForMonth(3, 15);
  const october1 = startOfMonth(getTargetDateForMonth(10, 1));

  if (!leihbarTermsOfAcceptance) {
    return false;
  }
  if (
    (isAfter(today, march15) && isBefore(new Date(leihbarTermsOfAcceptance.acceptedAt), march15)) ||
    (isAfter(today, october1) && isBefore(new Date(leihbarTermsOfAcceptance.acceptedAt), october1))
  ) {
    return false;
  }
  return true;
};

/**
 * Used to check if an Order has an overdue or conflicting Booking
 *
 * @param id
 * @param bookings
 * @param conflictingBookings
 * @returns
 */
export const getOrderRowClassName = (order: OrderWithConflicts) => {
  const hasOverdueBooking = order.bookings?.some(
    booking =>
      booking.state !== 'RETURNED' &&
      isBefore(new Date(booking.endTime), new Date()) &&
      booking.collectedAt &&
      !booking.deletedAt
  );

  const hasOverdueCollection = order.bookings?.some(
    booking => !booking.collectedAt && isBefore(new Date(booking.startTime), new Date())
  );

  if (hasOverdueBooking || hasOverdueCollection) {
    // If new Date() conflicts with another Booking
    const isConflicting = order.conflicts;
    if (isConflicting) {
      return 'conflicting';
    }
    return 'overdue';
  }
  return '';
};

export const isLenderOfProduct = (
  product: Product,
  userEntitlementGroups: EntitlementGroupInterface[]
) => {
  return userEntitlementGroups.some(eg => eg.id === product.lenderGroupId);
};
